<template>
  <view-container
    class="view-tools-leadlist"
    :breadcrumbs="breadcrumbs"
    header
    title="Lead List"
    subtitle="Integre seu provedor de email à Blitzpay para organizar seus leads."
  >
    <action-dispatcher action="product/get" :parameters="{ limit: 500 }">
      <form-group
        v-model="product_id"
        select
        filterable
        class="mt-5"
        id="product-select"
        placeholder="Selecionar um produto"
        :options="products.map(({ id, name }) => ({ id, name }))"
      />
    </action-dispatcher>

    <div v-if="product_id !== null">
      <div class="row mt-6">
        <div class="d-flex align-items-center col-lg-6 order-1 order-lg-0">
          <span class="table-quantity-counter mr-5">
            Total: {{ pagination.count }}
          </span>
          <button class="btn p-0 table-refresh" @click="refresh">
            <i class="fa-solid fa-refresh" />
          </button>
        </div>
        <div class="col-lg-6 d-flex align-items-center justify-content-end">
          <div class="d-none d-lg-flex ml-3">
            <button
              class="btn btn-primary btn-new-primary px-5 btn-40"
              @click="modal = true"
            >
              <i class="fa-solid fa-plus mr-6" />
              Adicionar
            </button>
          </div>
        </div>
      </div>

      <div class="col-12 d-lg-none my-6">
        <button
          class="btn btn-primary btn-new-primary btn-block btn-40"
          @click="modal = true"
        >
          <i class="fa-solid fa-plus mr-6" />
          Adicionar
        </button>
      </div>

      <div v-if="loading" class="text-center">
        <b-spinner variant="primary" />
      </div>

      <div
        v-else-if="!events.length"
        class="container centralize-row text-center pt-5"
      >
        <div class="row justify-content-center">
          <div class="col-lg-5">
            <img
              class="d-block mb-9 mx-auto"
              src="@/assets/images/home/welcome.png"
            />
            <h5>Você ainda não cadastrou nenhuma segmentação</h5>
            <div class="text-secondary mb-5">
              Clique no botão acima para cadastrar
            </div>
          </div>
        </div>
      </div>

      <div v-else>
        <data-table
          new-design
          class="d-none d-lg-block"
          :data="events"
          :columns="columns"
        >
          <template #default="{ item }">
            <div class="crop">
              <span>
                {{ item.name }}
              </span>
            </div>
            <div class="crop">
              <span>
                {{ formatEventName(item.event) }}
              </span>
            </div>
            <div class="crop">
              <span>
                {{ formatCommand(item.command) }}
              </span>
            </div>
            <div class="crop">
              <span>
                {{ formatProvider(item.integration) }}
              </span>
            </div>
            <div>
              <span :class="`${item.status}-badge`">
                {{ STATUS_MAP[item.status] }}
              </span>
            </div>
            <div class="d-flex align-items-center justify-content-end">
              <actions-dropdown
                :left="false"
                :right="true"
                :actions="getActions(item)"
                @delete="submitDelete(item)"
                @changeStatus="changeStatus(item)"
                @edit="edit(item)"
              />
            </div>
          </template>
        </data-table>
        <!-- mobile area -->
        <data-table
          new-design
          class="d-lg-none"
          :columns="['', '']"
          :data="events"
        >
          <template #default="{ item }">
            <div class="responsive-table">
              <span>
                <strong>Segmentação: </strong>
                {{ item.name }}
              </span>
              <span>
                <strong>Evento: </strong>
                {{ formatEventName(item.event) }}
              </span>
              <span>
                <strong>Comando:</strong>
                {{ formatCommand(item.command) }}
              </span>
              <span>
                <strong>Provedor: </strong>
                {{ formatProvider(item.integration) }}
              </span>
              <div>
                <strong>Status: </strong>
                <span :class="`${item.status}-badge`">
                  {{ STATUS_MAP[item.status] }}
                </span>
              </div>
            </div>
            <div class="d-flex justify-content-end">
              <actions-dropdown
                :left="false"
                :right="true"
                :actions="getActions(item)"
                @delete="submitDelete(item)"
                @changeStatus="changeStatus(item)"
                @edit="edit(item)"
              />
            </div>
          </template>
        </data-table>
        <!-- mobile area -->
      </div>

      <div class="d-flex justify-content-end py-3">
        <paginator
          v-show="events.length && !loading"
          :pagination="pagination"
          @on-update="onPaginationUpdate"
        />
      </div>

      <lead-list-modal
        v-model="modal"
        :product_id="product_id"
        :eventToEdit="eventToEdit"
        @refresh="refresh"
      />
    </div>
  </view-container>
</template>

<script>
import LeadListModal from "@/components/tools/modals/LeadList/Index.vue";
import get from "lodash/get";
import { mapState } from "vuex";

export default {
  components: {
    LeadListModal
  },
  data() {
    return {
      modal: false,
      loading: false,
      product_id: null,
      eventToEdit: null,
      columns: ["Segmentação", "Evento", "Comando", "Provedor", "Status", ""],
      params: {
        offset: 0,
        product_id: null
      },
      breadcrumbs: [
        { title: "Ferramentas", link: "tools" },
        { title: "Lead List" }
      ]
    };
  },
  mounted() {
    this.$store.dispatch("integration/getTypeEvents");
    this.$store.dispatch("integration/getTypeCommands");
    this.$store.dispatch("integration/getProviders", { limit: 500 });
  },
  watch: {
    product_id(v) {
      if (v) {
        this.dispatch();
      }
    },
    modal(v) {
      if (!v && this.eventToEdit) this.eventToEdit = null;
    }
  },
  computed: {
    ...mapState("product", ["products"]),
    ...mapState("integration", [
      "providers",
      "events",
      "pagination",
      "typeCommands",
      "typeEvents",
      "mapProviderName"
    ]),
    STATUS_MAP() {
      return {
        active: "Ativo",
        draft: "Rascunho",
        inactive: "Desativado"
      };
    }
  },
  methods: {
    getActions(item) {
      return [
        {
          text: "Editar",
          icon: "fa-solid fa-pen",
          confirmation: {
            enable: false
          },
          event: "edit"
        },
        {
          text:
            item.status === "draft" || item.status === "inactive"
              ? "Ativar"
              : "Desativar",
          icon: "fa-solid fa-power-off",
          confirmation: {
            enable: false
          },
          event: "changeStatus"
        },
        {
          text: "Excluir",
          class: "text-danger",
          icon: "fa-solid fa-trash",
          confirmation: {
            newDesign: true,
            submitButtonClass: "btn-danger",
            enable: true,
            text:
              "Deseja mesmo excluir esta integração? O blitzpay não enviará mais os leads para esta segmentação."
          },
          event: "delete"
        }
      ];
    },
    formatProvider(v) {
      return get(
        this.mapProviderName,
        get(
          this.providers.find(p => p.id === v),
          "name"
        ),
        "-"
      );
    },
    formatCommand(v) {
      return get(
        this.typeCommands.find(p => p.id === v),
        "name",
        "-"
      );
    },
    formatEventName(v) {
      return get(
        this.typeEvents.find(p => p.id === v),
        "name",
        "-"
      );
    },
    onPaginationUpdate(offset) {
      this.params.offset = offset;
      this.dispatch();
    },
    dispatch() {
      this.params.product_id = this.product_id;
      this.loading = true;
      this.$store
        .dispatch("integration/getLeadLists", this.params)
        .then()
        .catch(error => {
          this.$message.error("Não foi possível carregar os eventos");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submitDelete(v) {
      this.loading = true;
      this.$store
        .dispatch("integration/deleteEvents", v.id)
        .then(() => {
          this.dispatch();
          this.$message.success("Evento deletado!");
        })
        .catch(error => {
          this.$message.error("Não foi possível deletar o evento");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    changeStatus(item) {
      const status =
        item.status === "draft" || item.status === "inactive"
          ? "active"
          : "inactive";
      this.$store
        .dispatch("integration/putEvents", {
          id: item.id,
          status
        })
        .then(() => {
          this.$message.success(
            `O evento foi ${status === "active" ? "ativado" : "desativado"}`
          );
          this.refresh();
        })
        .catch(error => {
          this.$message.error("Erro ao salvar segmentação");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    edit(v) {
      this.eventToEdit = v;
      this.modal = true;
    },
    refresh() {
      this.dispatch();
    }
  }
};
</script>
<style lang="scss" scoped>
.view-tools-leadlist {
  ::v-deep {
    .el-input__inner {
      height: 40px;
      min-height: 40px;
    }
  }
}
</style>
